import React, { useState, useEffect } from 'react';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Checkbox,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axiosInstance from '../AxiosInstance';
import { trackReorderCandidatesViewed, trackReorderMarkedFulfilled } from '../services/analytics';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.dark,
  color: theme.palette.common.white,
  fontWeight: 'bold',
}));

export const ViewReorders = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMap, setSelectedMap] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [orderPrices, setOrderPrices] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [productNameFilter, setProductNameFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');

  useEffect(() => {
    const fetchOpenReorders = async () => {
      try {
        const response = await axiosInstance.get('/inventories/reorders/open');
        const items = response.data && Array.isArray(response.data.openReorders)
          ? response.data.openReorders
          : [];
        trackReorderCandidatesViewed();

        const initSelected = {};
        const initPrices = {};
        items.forEach((item) => {
          const key = item.reorderId;
          initSelected[key] = false;
          initPrices[key] =
            item.orderPrice !== undefined && item.orderPrice !== null
              ? item.orderPrice
              : 0;
        });

        setCandidates(items);
        setSelectedMap(initSelected);
        setOrderPrices(initPrices);
      } catch (error) {
        console.error('Error fetching open reorders:', error);
        setErrorMessage('Failed to fetch open reorders.');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchOpenReorders();
  }, []);

  const supplierOptions = React.useMemo(() => {
    const map = new Map();
    candidates.forEach((item) => {
      if (!item.supplierId) return;
      const label = item.supplierMarketName
        ? `${item.supplierName || 'Supplier'} (${item.supplierMarketName})`
        : (item.supplierName || 'Supplier');
      if (!map.has(item.supplierId)) {
        map.set(item.supplierId, label);
      }
    });
    return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
  }, [candidates]);

  const getFilteredCandidates = () => {
    let base = candidates;
    const trimmedNameFilter = productNameFilter.trim().toLowerCase();

    if (showFilters && (trimmedNameFilter || supplierFilter)) {
      base = base.filter((item) => {
        let matchesName = true;
        if (trimmedNameFilter) {
          const nameHaystack = `${item.productName || ''} ${item.brand || ''} ${item.variety || ''}`.toLowerCase();
          matchesName = nameHaystack.includes(trimmedNameFilter);
        }

        let matchesSupplier = true;
        if (supplierFilter) {
          matchesSupplier = String(item.supplierId) === String(supplierFilter);
        }

        return matchesName && matchesSupplier;
      });
    }

    return base;
  };

  const visibleCandidates = getFilteredCandidates();
  const allSelected =
    visibleCandidates.length > 0 &&
    visibleCandidates.every((item) => {
      const key = item.reorderId;
      return !!selectedMap[key];
    });

  const someSelected = visibleCandidates.some((item) => {
    const key = item.reorderId;
    return !!selectedMap[key];
  });

  const handleToggleAll = (event) => {
    const checked = event.target.checked;

    setSelectedMap((prev) => {
      const next = { ...prev };
      visibleCandidates.forEach((item) => {
        const key = item.reorderId;
        next[key] = checked;
      });
      return next;
    });
  };

  const handleToggleRow = (key) => {
    setSelectedMap((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleOrderPriceChange = (key, value) => {
    setOrderPrices((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmitClick = () => {
    const selectedItems = candidates.filter(item => {
      const key = item.reorderId;
      return selectedMap[key];
    });

    if (selectedItems.length === 0) {
      setErrorMessage('Please select at least one item to submit.');
      setSnackbarOpen(true);
      return;
    }

    setConfirmDialogOpen(true);
  };

  const handleConfirmSubmit = async () => {
    try {
      const selectedItems = candidates.filter(item => {
        const key = item.reorderId;
        return selectedMap[key];
      });

      const reorderIds = selectedItems.map((item) => item.reorderId);

      // Build a map of order price updates only for rows where the user changed the price
      const orderPriceUpdates = {};
      selectedItems.forEach((item) => {
        const key = item.reorderId;

        const original =
          item.orderPrice !== undefined && item.orderPrice !== null
            ? Number(item.orderPrice)
            : 0;

        const currentRaw = orderPrices[key];
        if (currentRaw === undefined || currentRaw === null || currentRaw === '') {
          return;
        }

        const current = Number(currentRaw);
        if (Number.isNaN(current)) {
          return;
        }

        if (current !== original) {
          orderPriceUpdates[item.reorderId] = current;
        }
      });

      await axiosInstance.post('/inventories/reorders/mark-fulfilled', {
        reorderIds,
        orderPrices: orderPriceUpdates,
      });

      setCandidates((prev) => prev.filter((item) => !reorderIds.includes(item.reorderId)));
      setSelectedMap((prev) => {
        const next = { ...prev };
        reorderIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });

      trackReorderMarkedFulfilled(null);
      setSuccessMessage('Selected reorders marked as fulfilled.');
      setErrorMessage('');
      setSnackbarOpen(true);
      setConfirmDialogOpen(false);
    } catch (error) {
      console.error('Error marking reorders as fulfilled:', error);
      setSuccessMessage('');
      setErrorMessage('Failed to update reorders. Please try again.');
      setSnackbarOpen(true);
      setConfirmDialogOpen(false);
    }
  };

  const handleCloseDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Container sx={{ marginTop: 4 }}>
      <Typography variant="h4" gutterBottom align="center">
        View Reorders
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 2 }} align="center">
        Submit reorder transactions
      </Typography>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : candidates.length === 0 ? (
        <Typography>No open reorders.</Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showFilters}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setShowFilters(checked);
                    if (!checked) {
                      setProductNameFilter('');
                      setSupplierFilter('');
                    }
                  }}
                />
              }
              label="Enable Filters"
            />
          </Box>

          {showFilters && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
                mb: 2,
                flexWrap: 'wrap',
              }}
            >
              <TextField
                label="Product Name"
                variant="outlined"
                size="small"
                value={productNameFilter}
                onChange={(e) => setProductNameFilter(e.target.value)}
              />
              <FormControl sx={{ minWidth: 200 }} size="small">
                <Select
                  displayEmpty
                  value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                >
                  <MenuItem value="">
                    <em>All suppliers</em>
                  </MenuItem>
                  {supplierOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <TableContainer component={Paper} sx={{ boxShadow: 3, marginBottom: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell padding="checkbox" sx={{ width: 110 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: 0.25,
                      }}
                    >
                      <Checkbox
                        indeterminate={someSelected && !allSelected}
                        checked={allSelected}
                        onChange={handleToggleAll}
                        sx={{
                          color: '#FFFFFF',
                          '&.Mui-checked': { color: '#FFFFFF' },
                          '&.MuiCheckbox-indeterminate': { color: '#FFFFFF' },
                        }}
                      />
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    Product Name
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    Order Price
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    Order Quantity
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    Current Stock
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    Unit
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    Supplier
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleCandidates.map((item) => {
                  const key = item.reorderId;
                  const brandVariety = [item.brand, item.variety].filter(Boolean).join(' - ');
                  const supplierLabel = item.supplierMarketName
                    ? `${item.supplierName || 'Supplier'} (${item.supplierMarketName})`
                    : (item.supplierName || 'Supplier');
                  const unitType = item.unitType || '';

                  return (
                    <TableRow
                      key={key}
                      hover
                      data-debug-product-id={item.productId}
                      data-debug-product-name={item.productName}
                      data-debug-current-stock={item.currentStock}
                      data-debug-stock-limit={item.stockLimit}
                      data-debug-target-stock={item.targetStock}
                      data-debug-reorder-quantity={item.orderQuantity}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={!!selectedMap[key]}
                          onChange={() => handleToggleRow(key)}
                        />
                      </TableCell>
                      <TableCell>
                        {item.productName}
                        {brandVariety && ` - ${brandVariety}`}
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          value={orderPrices[key] ?? 0}
                          onChange={(e) => handleOrderPriceChange(key, e.target.value)}
                          inputProps={{ min: 0, step: '0.01' }}
                          size="small"
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {item.orderQuantity}
                      </TableCell>
                      <TableCell align="center">
                        {item.currentStock}
                      </TableCell>
                      <TableCell align="center">
                        {unitType}
                      </TableCell>
                      <TableCell align="center">
                        {supplierLabel}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmitClick}
            >
              Fulfill Reorders
            </Button>
          </Box>

          <Dialog
            open={confirmDialogOpen}
            onClose={handleCloseDialog}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ textAlign: 'center' }}>Confirm Fulfilled Reorders</DialogTitle>
            <DialogContent sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <Typography gutterBottom>
                Are you sure you want to mark the following reorder(s) as fulfilled?
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell align="center">Product</StyledTableCell>
                      <StyledTableCell align="center">Order Quantity</StyledTableCell>
                      <StyledTableCell align="center">Unit</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {candidates
                      .filter(item => {
                        const key = item.reorderId;
                        return selectedMap[key];
                      })
                      .map((item) => {
                        const key = item.reorderId;
                        const unitType = item.unitType || '';
                        return (
                          <TableRow key={key}>
                            <TableCell align="center">{item.productName}</TableCell>
                            <TableCell align="center">{item.orderQuantity}</TableCell>
                            <TableCell align="center">{unitType}</TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button
                onClick={handleConfirmSubmit}
                variant="contained"
                color="primary"
              >
                Mark Fulfilled
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={handleCloseSnackbar}
          severity={successMessage ? 'success' : 'error'}
        >
          {successMessage || errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ViewReorders;
